import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  Row,
  Col,
  Space,
  Typography,
} from 'antd';
import { FileSearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import ProductImagesFormField from '../../../components/productImagesForField/ProductImagesFormField';
import { extractOrderFromImages } from '../../../utils/orderImageExtractor';
import { createOrderSchema, validateOrderForm } from '../../../utils/orderValidation';
import '../edit/OrderEdit.css';

const SOURCE_OPTIONS = [
  { value: 'K Etsy', label: 'K Etsy' },
  { value: 'Anirudh Etsy', label: 'Anirudh Etsy' },
  { value: 'Website', label: 'Website' },
  { value: 'Others', label: 'Others' },
];

const MODE_OF_PARCEL_OPTIONS = [
  { value: 'OnPoint', label: 'OnPoint' },
  { value: 'Sardar', label: 'Sardar' },
  { value: 'Others', label: 'Others' },
];

export default function CreateOrder({ inModal, onSuccess, onCancel }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [extractLoading, setExtractLoading] = useState(false);

  const dataURLtoBlob = (dataUrl) => {
    const arr = dataUrl.split(',');
    const mime = (arr[0].match(/:(.*?);/) || [])[1] || 'image/png';
    const bstr = atob(arr[1] || '');
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
    return new Blob([u8arr], { type: mime });
  };

  const applyExtractedToForm = (extracted) => {
    form.setFieldsValue({
      customerName: extracted.customerName || form.getFieldValue('customerName'),
      orderNumber: extracted.orderNumber || form.getFieldValue('orderNumber'),
      orderDate: extracted.orderDate ? dayjs(extracted.orderDate) : form.getFieldValue('orderDate'),
      targetDate: extracted.targetDate ? dayjs(extracted.targetDate) : form.getFieldValue('targetDate'),
      sourceOfOrder: extracted.sourceOfOrder ?? form.getFieldValue('sourceOfOrder'),
    });
  };

  const handleExtractFromImages = async () => {
    const images = form.getFieldValue('productImages');
    const list = Array.isArray(images) ? images : [];
    if (list.length === 0) {
      dispatch(setMessage({ message: 'Upload at least one image first', type: 'warning' }));
      return;
    }
    setExtractLoading(true);
    try {
      let extracted = null;
      try {
        const formData = new FormData();
        list.forEach((dataUrl, i) => {
          if (dataUrl && typeof dataUrl === 'string') formData.append('images', dataURLtoBlob(dataUrl), `image-${i}.png`);
        });
        const res = await axios.post('/orders/extract-from-images', formData);
        if (res.data) {
          if (res.data.data) extracted = res.data.data;
        }
      } catch (_) {
        // Fallback to client-side OCR
      }
      if (!extracted) extracted = await extractOrderFromImages(list);
      if (!extracted) {
        dispatch(setMessage({ message: 'No order details found in any image', type: 'info' }));
        return;
      }
      applyExtractedToForm(extracted);
      dispatch(setMessage({ message: 'Order details filled from image', type: 'success' }));
    } catch (e) {
      dispatch(setMessage({ message: e?.message || 'Failed to extract from images', type: 'error' }));
    } finally {
      setExtractLoading(false);
    }
  };

  const runValidation = async (valuesOverride) => {
    const values = valuesOverride ?? form.getFieldsValue();
    const fieldNames = Object.keys(createOrderSchema.fields);
    const fieldErrors = await validateOrderForm(createOrderSchema, values);
    if (fieldErrors) {
      const errorNames = new Set(fieldErrors.map((e) => e.name));
      const clearValid = fieldNames.filter((n) => !errorNames.has(n)).map((name) => ({ name, errors: [] }));
      form.setFields([...fieldErrors, ...clearValid]);
    } else {
      form.setFields(fieldNames.map((name) => ({ name, errors: [] })));
    }
  };

  const handleSubmit = async (values) => {
    const fieldErrors = await validateOrderForm(createOrderSchema, values);
    if (fieldErrors) {
      form.setFields(fieldErrors);
      dispatch(setMessage({ message: 'Please fix the form errors', type: 'warning' }));
      return;
    }
    setSubmitLoading(true);
    try {
      const productImages = Array.isArray(values.productImages) ? values.productImages : [];
      const payload = {
        customerName: values.customerName?.trim() ?? '',
        orderDate: values.orderDate ? dayjs(values.orderDate).toISOString() : null,
        targetDate: values.targetDate ? dayjs(values.targetDate).toISOString() : null,
        orderNumber: values.orderNumber?.trim() ?? '',
        sourceOfOrder: values.sourceOfOrder,
        modeOfParcel: values.modeOfParcel?.trim() ?? '',
        productImages,
      };
      await axios.post('/orders', payload);
      dispatch(setMessage({ message: 'Order created successfully', type: 'success' }));
      if (inModal && onSuccess) onSuccess();
      else navigate('/orders');
    } catch (err) {
      const msg = err.response?.data?.error || 'Create order failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="order-edit-page">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div className="order-edit-header">
          <div className="order-edit-header-left">
            <Typography.Title level={3} style={{ margin: 0 }}>
              New order
            </Typography.Title>
          </div>
          <Space>
            <Button
              type="primary"
              size="large"
              loading={submitLoading}
              onClick={() => form.submit()}
              className="app-btn-primary"
            >
              Create order
            </Button>
            <Button
              size="large"
              onClick={() => (inModal && onCancel ? onCancel() : navigate('/orders'))}
              className="app-btn-cancel"
            >
              Cancel
            </Button>
          </Space>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={(_changed, allValues) => setTimeout(() => runValidation(allValues), 0)}
          className="order-edit-form"
          initialValues={{
            customerName: '',
            orderNumber: '',
            orderDate: null,
            targetDate: null,
            sourceOfOrder: undefined,
            modeOfParcel: undefined,
            productImages: [],
          }}
        >
          <Card className="order-edit-card">
            <div className="order-edit-body">
              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item name="productImages" label="Product images">
                    <ProductImagesFormField className="order-edit-upload-large" />
                  </Form.Item>
                  <Button
                    type="default"
                    icon={<FileSearchOutlined />}
                    loading={extractLoading}
                    onClick={handleExtractFromImages}
                    style={{ marginBottom: 16 }}
                  >
                    Extract from images
                  </Button>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="customerName" label="Customer name" required>
                    <Input placeholder="Customer name" maxLength={200} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="orderNumber" label="Order number" required>
                    <Input placeholder="Order number" maxLength={100} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="orderDate" label="Order date" required>
                    <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="targetDate" label="Target date" required>
                    <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="sourceOfOrder" label="Source of order" required>
                    <Select placeholder="Select source" options={SOURCE_OPTIONS} allowClear={false} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item name="modeOfParcel" label="Mode of parcel">
                    <Select placeholder="Select mode" options={MODE_OF_PARCEL_OPTIONS} allowClear style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Card>
        </Form>
      </Space>
    </div>
  );
}
